import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { VideoTestData } from '../../types';
import { ABTestPerformanceTable } from '../ABTestPerformanceTable';
import { MetricsColumnFilter, MetricColumn } from '@/components/metrics/shared/MetricsColumnFilter';
import { PitchSelector } from '@/components/metrics/pitch/PitchSelector';
import { byPositionOptions, usePitchSelection } from '@/components/metrics/pitch/pitchSelection';

interface ABTestMetricsTabContentProps {
  videos: VideoTestData[];
  isEditable: boolean;
  onTrafficUpdate?: (videoId: string, percentage: number) => void;
  onPitchSaved?: () => void;
}

const defaultColumns: MetricColumn[] = [
  { id: 'views', label: 'Visualizações', visible: true },
  { id: 'plays', label: 'Plays', visible: true },
  { id: 'playsUnique', label: 'Plays Únicos', visible: true },
  { id: 'playRate', label: 'Taxa de Play', visible: true },
  { id: 'engagement', label: 'Engajamento', visible: true },
  { id: 'pitchRetention', label: 'Retenção no Pitch', visible: true },
  { id: 'buttonClicks', label: 'Cliques', visible: true },
];

export function ABTestMetricsTabContent({
  videos,
  isEditable,
  onTrafficUpdate,
  onPitchSaved
}: ABTestMetricsTabContentProps) {
  useLanguage();
  const [columns, setColumns] = useState<MetricColumn[]>(defaultColumns);

  // Seletor por posição (1º pitch, 2º pitch…): cada vídeo do teste tem os próprios pitches.
  const pitchOptions = byPositionOptions(videos.map((video) => video.pitches));
  const [pitchSelection, setPitchSelection] = usePitchSelection(pitchOptions);
  const pitchColumnVisible = !!columns.find((c) => c.id === 'pitchRetention')?.visible;

  return (
    <div className="space-y-6">
      {/* Modal Premium de Métricas */}
      <div className="bg-white rounded-[14px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-[#E7E9EF] overflow-hidden">
        {/* Header interno com filtros */}
        <div className="px-8 pt-8 pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">
              {translateInterfaceText("Desempenho por vídeo")}</h2>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("Acompanhe as métricas individuais de cada vídeo deste teste.")}</p>
          </div>

          {/* Seletor de pitch + filtro de colunas */}
          <div className="flex flex-shrink-0 items-center gap-3">
            {pitchColumnVisible && (
              <PitchSelector options={pitchOptions} value={pitchSelection} onChange={setPitchSelection} />
            )}
            <MetricsColumnFilter
              columns={columns}
              onColumnsChange={setColumns}
            />
          </div>
        </div>

        {/* Tabela Premium */}
        <div className="px-8 pb-8">
          <ABTestPerformanceTable
            onPitchSaved={onPitchSaved}
            pitchPosition={Number(pitchSelection ?? 0)}
            showPitchName={pitchOptions.length >= 2}
            videos={videos}
            isEditable={isEditable}
            onTrafficUpdate={onTrafficUpdate}
            columns={columns}
          />
        </div>
      </div>
    </div>
  );
}
